import { useState } from 'react'
import { useDispatch } from 'react-redux'
import { cartActions } from '../../../store/modules/cart/actions'
import { useToast } from '../../../contexts/ToastContext'
import orderService from '../../../services/orderService'
import { PaymentMethod } from '../../../shared/types/battery'
import { convertPaymentMethod } from '../../../shared/utils/checkoutHelpers'
import { getEffectiveUnitPrice } from '../../../constants/checkout'

/** Retorna a preferência efetiva de sucata do item. */
function getItemWillReturnScrap(item: { willReturnScrap?: boolean }, scrapPreference: boolean | null): boolean {
  return item.willReturnScrap ?? scrapPreference ?? true
}

interface CreateOrderParams {
  cartItems: any[]
  shippingAddress: any
  billingAddress: any
  selectedPaymentMethod: PaymentMethod
  deliveryMethod: 'shipping' | 'pickup' | null
  appliedCoupons: any[]
  calculateSubtotal: () => number
  calculateDiscount: () => number
  calculateShipping: () => number
  calculateTotal: () => number
  storeAddress: any
  isCampinas: boolean
  scrapPreference: boolean | null
}

export const useOrderCreation = () => {
  const [isCreatingOrder, setIsCreatingOrder] = useState(false)
  const [outOfStockItem, setOutOfStockItem] = useState<{ id: string; name: string } | null>(null)
  const { addToast } = useToast()
  const dispatch = useDispatch()

  const clearOutOfStockError = () => setOutOfStockItem(null)

  const createOrder = async (params: CreateOrderParams): Promise<string | null> => {
    const {
      cartItems,
      shippingAddress,
      billingAddress,
      selectedPaymentMethod,
      deliveryMethod,
      appliedCoupons,
      calculateSubtotal,
      calculateDiscount,
      calculateShipping,
      calculateTotal,
      storeAddress,
      isCampinas,
      scrapPreference
    } = params

    setIsCreatingOrder(true)
    setOutOfStockItem(null)
    try {
      // ... (rest of the logic stays same until catch)
      // Definir endereço de entrega com base no método de envio
      let finalShippingAddress = {
        name: shippingAddress.street || 'Endereço de entrega',
        street: shippingAddress.street,
        number: shippingAddress.number,
        complement: shippingAddress.complement || '',
        neighborhood: shippingAddress.neighborhood,
        city: shippingAddress.city,
        state: shippingAddress.state,
        zipCode: shippingAddress.zipCode.replace(/\D/g, ''),
        country: shippingAddress.country || 'Brasil'
      }

      // Se for retirada, usar o endereço da loja
      if (deliveryMethod === 'pickup') {
        finalShippingAddress = {
          name: 'Retirada na Loja',
          street: storeAddress.street,
          number: storeAddress.number,
          complement: storeAddress.complement || '',
          neighborhood: storeAddress.neighborhood,
          city: storeAddress.city,
          state: storeAddress.state,
          zipCode: storeAddress.zipCode.replace(/\D/g, ''),
          country: storeAddress.country || 'Brasil'
        }
      }

      const orderData = {
        items: cartItems.map(item => {
          const allowsScrap = (item.product as { allowsScrapReturn?: boolean }).allowsScrapReturn === true
          const willReturn = getItemWillReturnScrap(item, scrapPreference)
          const price = allowsScrap
            ? getEffectiveUnitPrice(
                item.product.price,
                item.product.weight ?? 0,
                isCampinas,
                willReturn
              )
            : item.product.price
          return { batteryId: item.product.id, quantity: item.quantity, price }
        }),
        shippingAddress: finalShippingAddress,
        billingAddress: {
          name: billingAddress.street || 'Endereço de cobrança',
          street: billingAddress.street || shippingAddress.street,
          number: billingAddress.number || shippingAddress.number,
          complement: billingAddress.complement || shippingAddress.complement || '',
          neighborhood: billingAddress.neighborhood || shippingAddress.neighborhood,
          city: billingAddress.city || shippingAddress.city,
          state: billingAddress.state || shippingAddress.state,
          zipCode: (billingAddress.zipCode || shippingAddress.zipCode).replace(/\D/g, ''),
          country: billingAddress.country || shippingAddress.country || 'Brasil'
        },
        paymentMethod: convertPaymentMethod(selectedPaymentMethod) as any,
        shippingMethod: deliveryMethod === 'pickup' ? 'pickup' : 'delivery',
        appliedCoupons: appliedCoupons.map(c => c.code),
        subtotal: calculateSubtotal(),
        discount: calculateDiscount(),
        shipping: deliveryMethod === 'pickup' ? 0 : calculateShipping(),
        total: calculateTotal()
      }

      const response = await orderService.create(orderData as any)
      
      if (response?.order?.id) {
        return response.order.id
      }
      return null
    } catch (error: any) {
      console.error('Erro ao criar pedido:', error)
      
      const message = error.message || 'Não foi possível criar o pedido. Tente novamente.'
      
      if (error.status === 409) {
        // Tentar identificar o produto pelo nome na mensagem
        // "Estoque insuficiente para o produto Bateria Heliar H40JD"
        const productNameMatch = message.match(/produto (.+)$/)
        const productName = productNameMatch ? productNameMatch[1] : ''
        
        const item = cartItems.find(i => i.product.name === productName)
        
        if (item) {
          setOutOfStockItem({ id: item.product.id, name: item.product.name })
        } else {
          // Fallback se não encontrar por nome, mostrar toast e abrir cart como antes
          addToast({
            type: 'error',
            title: 'Estoque Insuficiente',
            message: message
          })
          dispatch(cartActions.openCart())
        }
      } else {
        addToast({
          type: 'error',
          title: 'Erro',
          message: message
        })
      }

      return null
    } finally {
      setIsCreatingOrder(false)
    }
  }

  return {
    createOrder,
    isCreatingOrder,
    outOfStockItem,
    clearOutOfStockError
  }
}
